import React, { useMemo } from "react";
import { FeatherAlertCircle } from "@subframe/core";
import { Table, Badge } from "../../ui";
import { CustomerPaymentCategoryDetails } from "../../services/api";

interface CustomerPaymentViewContentProps {
  customerName: string;
  categoryType: string;
  advancePayment: number;
  invoices: CustomerPaymentCategoryDetails["invoices"];
  isLoading: boolean;
  transactionCurrencySymbol?: string;
  transactionAmount?: number;
  exchangeRate?: number | null;
  bankCharges?: number | null;
}

const CustomerPaymentViewContent: React.FC<CustomerPaymentViewContentProps> = ({
  customerName,
  categoryType,
  advancePayment,
  invoices,
  isLoading,
  transactionCurrencySymbol,
  exchangeRate,
  bankCharges,
}) => {
  // Helper function to format numbers with 2 decimal places
  const formatAmount = (amount: number): string => {
    return amount.toLocaleString("en-IN", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  const formattedCategoryType = useMemo(() => {
    if (!categoryType) return "";
    return categoryType
      .split("_")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  }, [categoryType]);

  const currencySymbol =
    invoices[0]?.currency_symbol || transactionCurrencySymbol || "₹";
  const totalApplied = invoices.reduce(
    (sum, invoice) => sum + (invoice.amount_applied || 0),
    0,
  );
  const totalAdjustment = invoices.reduce(
    (sum, invoice) => sum + (invoice.adjustment_amount || 0),
    0,
  );

  const formatExchangeRate = (rate: number): string => {
    const truncated = Math.trunc(rate * 1_000_000) / 1_000_000;
    return truncated.toString();
  };

  const showExchangeRate =
    exchangeRate != null && exchangeRate > 0;
  const showBankCharges = bankCharges != null && bankCharges > 0;
  const showForexSummary = showExchangeRate || showBankCharges;

  if (isLoading) {
    return (
      <div className="flex w-full items-center justify-center rounded-md border border-dashed border-neutral-border bg-neutral-50 px-6 py-10 text-body text-subtext-color">
        Loading customer payment details...
      </div>
    );
  }

  return (
    <div className="flex w-full flex-col gap-4 rounded-md border border-solid border-neutral-border bg-neutral-50 px-6 py-6">
      <div className="flex w-full items-center justify-between gap-4">
        <div className="flex flex-col gap-1">
          <span className="text-heading-3 font-heading-3 text-default-font">
            {formattedCategoryType || "Customer Payment"}
          </span>
          <div className="flex flex-wrap items-center gap-2">
            {customerName && (
              <Badge className="border-success-700 bg-success-700 text-white [&>span]:text-white">
                Customer: {customerName}
              </Badge>
            )}
          </div>
        </div>
      </div>

      {invoices.length === 0 ? (
        <div className="flex w-full flex-col items-center gap-2 rounded border border-dashed border-neutral-border px-4 py-6 text-center text-body text-subtext-color">
          <FeatherAlertCircle className="text-neutral-400" />
          <span>No invoices linked to this transaction.</span>
        </div>
      ) : (
        <Table
          header={
            <Table.HeaderRow>
              <Table.HeaderCell>Invoice #</Table.HeaderCell>
              <Table.HeaderCell>Total</Table.HeaderCell>
              <Table.HeaderCell>Balance</Table.HeaderCell>
              <Table.HeaderCell>Amount Applied</Table.HeaderCell>
              <Table.HeaderCell>TDS Details</Table.HeaderCell>
            </Table.HeaderRow>
          }
        >
          {invoices.map((invoice, index) => {
            const invoiceCurrencySymbol =
              invoice.currency_symbol || currencySymbol;
            return (
              <Table.Row key={index}>
                <Table.Cell>
                  <span className="text-body-bold">
                    {invoice.invoice_number}
                  </span>
                </Table.Cell>
                <Table.Cell>
                  {invoice.total !== null && invoice.total !== undefined ? (
                    <span className="text-body">
                      {invoiceCurrencySymbol} {formatAmount(invoice.total)}
                    </span>
                  ) : (
                    <span className="text-caption text-subtext-color">—</span>
                  )}
                </Table.Cell>
                <Table.Cell>
                  <span className="text-body">
                    {invoiceCurrencySymbol}{" "}
                    {formatAmount(invoice.balance || 0)}
                  </span>
                </Table.Cell>
                <Table.Cell>
                  <div className="flex flex-col gap-0.5">
                    <span className="text-body-bold text-success-600">
                      {invoiceCurrencySymbol}{" "}
                      {formatAmount(invoice.amount_applied || 0)}
                    </span>
                    {(invoice.adjustment_amount || 0) > 0 && (
                      <span className="text-caption text-subtext-color">
                        Adjustment: {invoiceCurrencySymbol}{" "}
                        {formatAmount(invoice.adjustment_amount || 0)}
                      </span>
                    )}
                  </div>
                </Table.Cell>
                <Table.Cell>
                  {invoice.tds_tax_amount ? (
                    <div className="flex flex-col text-caption text-subtext-color">
                      <span>
                        {invoice.tds_tax_name || "TDS"} (
                        {invoice.tds_tax_percentage?.toFixed(2)}%)
                      </span>
                      <span>
                        Amount: {invoiceCurrencySymbol}
                        {formatAmount(invoice.tds_tax_amount)}
                      </span>
                      {invoice.tds_section && (
                        <span>Section: {invoice.tds_section}</span>
                      )}
                    </div>
                  ) : (
                    <span className="text-caption text-subtext-color">
                      Not applied
                    </span>
                  )}
                </Table.Cell>
              </Table.Row>
            );
          })}
        </Table>
      )}

      <div className="grid w-full grid-cols-1 gap-4 md:grid-cols-3">
        <div className="flex flex-col rounded-md border border-dashed border-neutral-border px-4 py-3">
          <span className="text-caption text-subtext-color">
            Amount Applied
          </span>
          <span className="text-heading-4 font-heading-4 text-default-font">
            {currencySymbol} {formatAmount(totalApplied)}
          </span>
        </div>
        <div className="flex flex-col rounded-md border border-dashed border-neutral-border px-4 py-3">
          <span className="text-caption text-subtext-color">Adjustment</span>
          <span className="text-heading-4 font-heading-4 text-default-font">
            {currencySymbol} {formatAmount(totalAdjustment)}
          </span>
        </div>
        <div className="flex flex-col rounded-md border border-dashed border-neutral-border px-4 py-3">
          <span className="text-caption text-subtext-color">
            Advance Payment
          </span>
          <span className="text-heading-4 font-heading-4 text-default-font">
            {currencySymbol} {formatAmount(advancePayment)}
          </span>
        </div>
      </div>

      {showForexSummary && (
        <div className="grid w-full grid-cols-1 gap-4 md:grid-cols-2">
          {showExchangeRate && (
            <div className="flex flex-col rounded-md border border-dashed border-neutral-border px-4 py-3">
              <span className="text-caption text-subtext-color">
                Exchange Rate
              </span>
              <span className="text-heading-4 font-heading-4 text-default-font">
                {formatExchangeRate(exchangeRate!)}
              </span>
            </div>
          )}
          {showBankCharges && (
            <div className="flex flex-col rounded-md border border-dashed border-neutral-border px-4 py-3">
              <span className="text-caption text-subtext-color">
                Bank Charges
              </span>
              <span className="text-heading-4 font-heading-4 text-default-font">
                {currencySymbol} {formatAmount(bankCharges!)}
              </span>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default CustomerPaymentViewContent;
